import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { DigitalMembershipCard } from "@/components/DigitalMembershipCard";

const INTERESTS = ["Education", "Environment", "Health", "Disaster Relief", "Women's Empowerment", "Youth Leadership", "Digital Literacy", "Consumer Rights", "Legal Awareness"];

export const MembershipForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", city: "", state: "", dob: "", membership_type: "Individual", interests: [], pledge_agreed: true, photo_b64: null });
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggle = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo must be under 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      // Downscale to keep the card crisp but the payload small
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const S = 300;
        canvas.width = S; canvas.height = S;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, S, S);
        setForm((f) => ({ ...f, photo_b64: canvas.toDataURL("image/jpeg", 0.85) }));
        toast.success("Photo added to your card");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/memberships", form);
      setMember(res);
      toast.success("Welcome to CAF. Your Digital Membership Card is below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please check your inputs.");
    } finally { setLoading(false); }
  };

  if (member) return (
    <div className="grid gap-6 lg:grid-cols-2 items-start" data-testid="membership-form-success">
      <div className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-6">
        <h3 className="heading-3">Membership activated — {member.member_id}</h3>
        <p className="lead mt-2">Welcome, {member.full_name}. Download your Digital Membership Card and share it with pride. Your onboarding email (with training and volunteer paths) will arrive within 24 hours.</p>
        <ul className="mt-3 text-sm text-[hsl(var(--muted-ink))] list-disc pl-5 space-y-1">
          <li>Your Member ID <strong>{member.member_id}</strong> is saved with CAF — quote it anytime.</li>
          <li>Access foundational training modules.</li>
          <li>Join local field programs based on your interests.</li>
        </ul>
      </div>
      <div className="flex justify-center"><DigitalMembershipCard member={member} showActions /></div>
    </div>
  );

  return (
    <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2" data-testid="membership-form">
      <Field label="Full name*"><Input required data-testid="membership-form-full_name" value={form.full_name} onChange={set("full_name")} /></Field>
      <Field label="Email*"><Input required type="email" data-testid="membership-form-email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Phone*"><Input required data-testid="membership-form-phone" value={form.phone} onChange={set("phone")} /></Field>
      <Field label="Date of birth"><Input type="date" data-testid="membership-form-dob" value={form.dob} onChange={set("dob")} /></Field>
      <Field label="City*"><Input required data-testid="membership-form-city" value={form.city} onChange={set("city")} /></Field>
      <Field label="State*"><Input required data-testid="membership-form-state" value={form.state} onChange={set("state")} /></Field>
      <Field label="Membership type">
        <Select value={form.membership_type} onValueChange={set("membership_type")}>
          <SelectTrigger data-testid="membership-form-type"><SelectValue /></SelectTrigger>
          <SelectContent>{["Individual", "Student", "Life Member", "Corporate"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
        </Select>
      </Field>
      <div className="md:col-span-2">
        <Label className="text-sm font-medium text-foreground">Your photo (optional — appears on your card)</Label>
        <div className="mt-2 flex items-center gap-3">
          {form.photo_b64 && <img src={form.photo_b64} alt="preview" className="h-14 w-14 rounded-lg object-cover ring-2 ring-[hsl(var(--orange))]" data-testid="membership-photo-preview" />}
          <Input type="file" accept="image/*" data-testid="membership-photo-input" onChange={onPhoto} className="max-w-xs" />
          {form.photo_b64 && <button type="button" className="text-xs text-[hsl(var(--muted-ink))] underline" onClick={() => setForm((f) => ({ ...f, photo_b64: null }))}>Remove</button>}
        </div>
      </div>
      <div className="md:col-span-2">
        <Label className="text-sm font-medium text-foreground">Interests</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {INTERESTS.map((i) => (
            <label key={i} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs cursor-pointer ${form.interests.includes(i) ? "border-foreground bg-[hsl(var(--surface-tint))] text-foreground" : "border-border text-[hsl(var(--muted-ink))] hover:border-foreground"}`}>
              <Checkbox data-testid={`membership-interest-${i.toLowerCase().replace(/[^a-z]/g,"-")}`} checked={form.interests.includes(i)} onCheckedChange={() => toggle(i)} className="h-3.5 w-3.5" />{i}
            </label>
          ))}
        </div>
      </div>
      <div className="md:col-span-2 flex items-start gap-2">
        <Checkbox data-testid="membership-pledge" checked={form.pledge_agreed} onCheckedChange={(v) => set("pledge_agreed")(!!v)} id="pledge" />
        <Label htmlFor="pledge" className="text-xs text-[hsl(var(--muted-ink))]">I pledge to uphold CAF’s Code of Ethics and Volunteer Policy — lawful, non-partisan and non-violent civic action.</Label>
      </div>
      <div className="md:col-span-2 flex justify-end"><Button disabled={loading || !form.pledge_agreed} type="submit" data-testid="membership-form-submit" className="btn-primary">{loading ? "Activating..." : "Activate Digital Membership"}</Button></div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-sm font-medium text-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>
);
